import FormFooter from "@/components/form-footer";
import cn from "@/utils/class-names";
import * as React from "react";
import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { routes } from "@/config/routes";
import useApi from "@/hooks/useApi";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import {
  CreateSiteContentInput,
  createSiteContentSchema,
} from "@/utilities/validators/site-content";
import SiteContentRepository from "@/utilities/repositories/SiteContent";
import { Button, Input, Textarea } from "rizzui";
import QuillEditor from "@/components/ui/quille-editor/quill-editor";
import SingleImageUpload from "@/components/ui/file-upload/single-image-upload";

export interface CreateEditSiteContentProps {
  className?: string;
  defaultValues?: Partial<CreateSiteContentInput>;
}

export default function CreateEditSiteContent({
  className,
  defaultValues,
}: CreateEditSiteContentProps) {
  const [isHtmlSource, setIsHtmlSource] = useState(false);
  const methods = useForm<CreateSiteContentInput>({
    resolver: zodResolver(createSiteContentSchema),
    reValidateMode: "onChange",
    mode: "all",
    defaultValues,
  });

  const handleBack = () => {
    methods.reset();
    navigate(routes.siteContent.listing);
  };

  const navigate = useNavigate();

  const { request: createSiteContent, loading: loadingCreate } = useApi({
    request: SiteContentRepository.createSiteContent,
  });

  const { request: updateSiteContent, loading: loadingEdit } = useApi({
    request: SiteContentRepository.updateSiteContent,
  });

  const { id } = useParams();

  const isUpdate = !!id;

  const onSubmit = async (data: CreateSiteContentInput) => {
    try {
      const payload = {
        ...data,
        featuredImageId: data.featuredImageId?.trim() || undefined,
        featuredImageUrl: data.featuredImageUrl?.trim() || undefined,
      };

      if (isUpdate) {
        await updateSiteContent(id, payload);
      } else {
        await createSiteContent(payload);
      }
      toast.success("Success");
      handleBack();
    } catch (error: any) {
      toast.error(error?.message || "Something went wrong");
    }
  };
  const isLoading = loadingCreate || loadingEdit;

  return (
    <FormProvider {...methods}>
      <form
        className={cn("mt-6", className)}
        noValidate
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <div className="[&>div:last-child]:pb-28 mb-10 h-full grid gap-6 divide-y divide-dashed divide-gray-200">
          <div className="pt-6">
            <Input
              label="Page Title"
              placeholder="Enter page title"
              {...methods.register("title")}
              error={methods?.formState?.errors.title?.message as string}
            />
          </div>

          <div className="pt-6">
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Page Content
              </label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setIsHtmlSource((value) => !value)}
              >
                {isHtmlSource ? "Use WYSIWYG Editor" : "Insert HTML Code"}
              </Button>
            </div>
            {isHtmlSource ? (
              <Textarea
                {...methods.register("content")}
                rows={14}
                placeholder="Paste HTML or an approved embed code here"
                error={methods?.formState?.errors?.content?.message as string}
              />
            ) : (
              <Controller
                control={methods?.control}
                name="content"
                render={({ field: { onChange, value } }) => (
                  <QuillEditor
                    value={value}
                    onChange={onChange}
                    label=""
                    className="col-span-full [&_.ql-editor]:min-h-[180px]"
                    labelClassName="font-medium text-gray-700 dark:text-gray-600 mb-1.5"
                    error={methods?.formState?.errors?.content?.message as string}
                  />
                )}
              />
            )}
          </div>

          <div className="grid gap-6 pt-6 md:grid-cols-2">
            <Input
              label="Meta-Title"
              placeholder="SEO page title"
              {...methods.register("metaTitle")}
              error={methods?.formState?.errors.metaTitle?.message as string}
            />
            <Textarea
              label="Meta-Description"
              placeholder="SEO page description"
              rows={3}
              {...methods.register("metaDescription")}
              error={
                methods?.formState?.errors.metaDescription?.message as string
              }
            />
          </div>

          <div className="pt-6">
            <Controller
              control={methods.control}
              name="featuredImageId"
              render={({ field, fieldState }) => (
                <SingleImageUpload
                  title="Featured Image"
                  imageUrl={methods.watch("featuredImageUrl") || ""}
                  setValue={(value) => {
                    field.onChange(value.id || undefined);
                    methods.setValue("featuredImageUrl", value.url, {
                      shouldDirty: true,
                    });
                  }}
                  error={fieldState.error?.message}
                  uploaderText="Upload a featured image"
                />
              )}
            />
          </div>
        </div>
        <FormFooter
          isLoading={isLoading}
          altBtnText="Back"
          submitBtnText={"Save"}
          handleAltBtn={handleBack}
          submitButtonProps={{
            disabled: isLoading || !methods.formState.isDirty,
          }}
        />
      </form>
    </FormProvider>
  );
}
